import React from 'react';

import ClearIcon from '@mui/icons-material/Clear';
import {
	FormControl,
	FormHelperText,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	SelectChangeEvent,
	SelectProps,
	SxProps,
} from '@mui/material';
import { FieldConfig, FieldProps, Field as FormikField } from 'formik';

export interface ISelectFieldOption {
	label?: string;
	value: string | number;
	disabled?: boolean;
}

export type TSelectFieldProps = SelectProps &
	FieldConfig & {
		options?: ISelectFieldOption[];
		сlearable?: boolean;
		withMaxHeight?: boolean;
		required?: boolean;
		fullWidth?: boolean;
		storeArrayValue?: boolean;

		minSelected?: number;
		maxSelected?: number;

		minErrorText?: string;
		maxErrorText?: string;
		labelSx?: SxProps;
	};

function SelectField({
	name,
	label,
	options = [],
	placeholder,
	displayEmpty,
	onChange,
	сlearable = false,
	withMaxHeight = false,
	required = false,
	fullWidth = true,
	storeArrayValue = false,
	minSelected,
	maxSelected,
	minErrorText,
	maxErrorText,
	labelSx,
	...props
}: TSelectFieldProps) {
	const SELECT_ALL_VALUE = '__all__';

	const renderOption = (option: ISelectFieldOption) => (
		<MenuItem value={option.value} key={option.value} disabled={option.disabled}>
			{option.label}
		</MenuItem>
	);

	const toArrayValue = (val: any): any[] => {
		if (Array.isArray(val)) return val;
		if (val === '' || val == null) return [];
		return [val];
	};

	const toSingleValueForUI = (val: any): string | number | '' => {
		if (Array.isArray(val)) return val[0] ?? '';
		return val ?? '';
	};

	const normalizeError = (err: unknown): string | undefined => {
		if (!err) return undefined;
		if (typeof err === 'string') return err;
		if (Array.isArray(err)) return err.filter(Boolean).join(', ');
		if (typeof err === 'object') {
			const first = Object.values(err as Record<string, any>)[0];
			return typeof first === 'string' ? first : undefined;
		}
		return undefined;
	};

	return (
		<FormikField
			name={name}
			validate={(value: any) => {
				if (!required) return undefined;
				if (value === null || value === '') return `The ${name} field is required`;
				if (Array.isArray(value) && value.length === 0) return `The ${name} field is required`;
				return undefined;
			}}
		>
			{({ field, meta }: FieldProps) => {
				const [limitError, setLimitError] = React.useState<string | undefined>(undefined);

				const isMultiple = Boolean(props.multiple);
				const prevArray = isMultiple ? toArrayValue(field.value) : [];
				const prevLen = prevArray.length;
				const raw = field.value;

				let uiValue: any;
				if (isMultiple) {
					uiValue = toArrayValue(raw);
				} else if (storeArrayValue) {
					uiValue = toSingleValueForUI(raw);
				} else {
					uiValue = raw ?? '';
				}

				const requiredErrorText = normalizeError(meta.error);
				const helperText = limitError ?? requiredErrorText;
				const hasError = Boolean(helperText);

				const validateCount = (prevLength: number, nextLength: number) => {
					if (typeof maxSelected === 'number' && nextLength > maxSelected) {
						setLimitError(maxErrorText ?? `You can select up to ${maxSelected}.`);
						return false;
					}

					const isDeselecting = nextLength < prevLength;

					if (typeof minSelected === 'number' && isDeselecting && nextLength < minSelected) {
						setLimitError(minErrorText ?? `Select at least ${minSelected}.`);
						return false;
					}
					if (limitError) setLimitError(undefined);
					return true;
				};

				const handleClear = () => {
					if (isMultiple && typeof minSelected === 'number' && minSelected > 0) {
						setLimitError(minErrorText ?? `Select at least ${minSelected}.`);
						return;
					}
					let cleared: any;
					if (isMultiple) cleared = [];
					else if (storeArrayValue) cleared = [];
					else cleared = '';
					const event = { target: { name: field.name, value: cleared } };
					field.onChange(event as any);
					onChange && onChange(event as any, null as any);
					setLimitError(undefined);
				};

				const showPlaceholder = !isMultiple && Boolean(placeholder || displayEmpty);
				const paperBase = {
					sx: {
						backgroundColor: 'background.paper',
						backgroundImage: 'none',
						color: 'text.primary',
						backdropFilter: 'none',
					} as const,
				};
				const menuProps = withMaxHeight
					? { slotProps: { paper: { ...paperBase, style: { maxHeight: 300 } } } }
					: { slotProps: { paper: paperBase } };

				const hasValue = isMultiple ? (uiValue as any[]).length > 0 : uiValue !== '';

				return (
					<FormControl fullWidth={fullWidth} error={hasError} required={required}>
						{label ? (
							<InputLabel sx={labelSx} size={props.size === 'small' ? 'small' : 'normal'}>
								{label}
							</InputLabel>
						) : null}

						<Select
							label={label}
							error={hasError}
							{...field}
							value={uiValue}
							displayEmpty={showPlaceholder}
							MenuProps={menuProps}
							{...props}
							onChange={(event, child) => {
								const selected = (event.target as any).value;

								if (Array.isArray(selected) && selected.includes(SELECT_ALL_VALUE)) {
									const allValues = options
										.filter((opt) => opt.value !== SELECT_ALL_VALUE)
										.map((opt) => opt.value);
									if (!validateCount(prevLen, allValues.length)) return;
									const eventAll = { ...event, target: { name: field.name, value: allValues } };
									field.onChange(eventAll as any);
									onChange && onChange(eventAll as SelectChangeEvent<any>, child);
									return;
								}

								if (isMultiple) {
									const nextArray = toArrayValue(selected);
									if (!validateCount(prevLen, nextArray.length)) return;
									const wrap = { ...event, target: { name: field.name, value: nextArray } };
									field.onChange(wrap as any);
									onChange && onChange(wrap as SelectChangeEvent<any>, child);
									return;
								}

								if (storeArrayValue && !Array.isArray(selected)) {
									const arr = selected === '' || selected == null ? [] : [selected];
									const wrap = { ...event, target: { name: field.name, value: arr } };
									field.onChange(wrap as any);
									onChange && onChange(wrap as SelectChangeEvent<any>, child);
									setLimitError(undefined);
								} else {
									field.onChange(event);
									onChange && onChange(event, child);
									setLimitError(undefined);
								}
							}}
						>
							{showPlaceholder && (
								<MenuItem value="" disabled>
									{placeholder}
								</MenuItem>
							)}
							{options.map(renderOption)}
						</Select>

						<FormHelperText>{helperText}</FormHelperText>

						{hasValue && сlearable && (
							<IconButton onClick={handleClear} sx={{ position: 'absolute', top: 10, right: 25 }}>
								<ClearIcon />
							</IconButton>
						)}
					</FormControl>
				);
			}}
		</FormikField>
	);
}

export default SelectField;
